import {defineStore} from "pinia";
import axios from "axios";
import {handleError, useInitializeStore} from "./storeHelper";
import {BTableOptions, serializeSortBy} from "@/components/BTable/BTable.types";
import {computed, reactive, ref} from "vue";
import {
    BFieldSelection,
    BFilterableField,
    BFilterOptions,
    decimalOperations,
    listOperations,
    textOperations,
} from "@/components/BFilterEditor/BFilterEditor.types";
import type {Dataset, Vendor, VendorOri} from "@/types";
import {serializeFilters} from "@/components/BTable/HelperFunctions";
import {PagedList} from "@/audit.types";
import {useGlobalMessagesStore} from "./globalMessagesStore";
import {clone} from "@/utils";
import { useOriStore } from "./oriStore";

export const useVendorOriStore = defineStore("vendorOriStore", () => {
    const globalMessages = useGlobalMessagesStore();
    const oriStore = useOriStore();
    const apiUrl = "api/v1/vendor/";
    const loading = ref<boolean>(false);
    const items = ref<VendorOri[]>();
    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const defaultTableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [
            {
                isDescending: false,
                key: "oriName",
            },
        ],
        filterBy: [],
        pagingMode: "withTotal",
    } as BTableOptions);
    const tableOptions = ref<BTableOptions>(clone(defaultTableOptions.value));

    const roles = ref<BFieldSelection[]>();
    const filterableFields = ref<BFilterableField[]>();

    useInitializeStore(async () => {

        let datasets= await axios.get<Dataset[]>(`${apiUrl}/datasets`);
        filterableFields.value = reactive<BFilterableField[]>([
            {
                property: "ori",
                label: "Agency",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: oriStore.getOrisAsBFieldSelectionArray,
            },
             {
                property: "dataCollection",
                label: "Data Collection",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: datasets.data.map(it => ({
                    text: it.label,
                    value: it.code
                } as BFieldSelection)),
            },
        ]);
    });

    const reset = async () => {
        filterOptions.value.filters = [];        
        Object.assign(tableOptions.value, defaultTableOptions.value);
    };

    const list = async (vendorCode: string) => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields.value!);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<VendorOri>>(`${apiUrl}${vendorCode}/agencies`, {
                params: {
                    pageSize: tableOptions.value.itemsPerPage,
                    page: tableOptions.value.page,
                    sort: sortParam,
                    filter: filterParam,
                },
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    return {
        list,
        reset,
        items,
        filterableFields,
        filterOptions,
        tableOptions,
        roles,
        loading,
    };
});
